import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { 
  UserPlus, 
  ShoppingCart, 
  CheckCircle, 
  Shield, 
  Globe, 
  FileText,
  ArrowRight,
  Leaf,
  Recycle,
  ChevronDown,
  Send,
  Loader2
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Scroll animation hook
function useScrollAnimation() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}

// FAQ Accordion Item
function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="accordion-item">
      <button 
        className="accordion-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-lg">{question}</span>
        <ChevronDown className={`h-5 w-5 text-[#056839] accordion-icon ${isOpen ? 'open' : ''}`} />
      </button>
      <div className={`accordion-content ${isOpen ? 'open' : ''}`}>
        <p className="text-[#3F3F3F] leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const stepsAnimation = useScrollAnimation();
  const featuresAnimation = useScrollAnimation();
  const pricingAnimation = useScrollAnimation();
  const faqAnimation = useScrollAnimation();
  const partnersAnimation = useScrollAnimation();
  const contactAnimation = useScrollAnimation();

  const steps = [
    { 
      icon: UserPlus, 
      title: 'Create Account', 
      description: 'Register your company in minutes with a simple onboarding process' 
    },
    { 
      icon: ShoppingCart, 
      title: 'Choose Service', 
      description: 'Select the compliance package that fits your business needs' 
    },
    { 
      icon: CheckCircle, 
      title: 'Stay Compliant', 
      description: 'Let us handle your EPR obligations while you focus on your business' 
    },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Full Compliance',
      description: 'Meet all regulatory requirements with our comprehensive EPR services for plastic packaging and WEEE',
      image: 'https://images.pexels.com/photos/11412596/pexels-photo-11412596.jpeg?auto=compress&cs=tinysrgb&w=400&h=200&fit=crop',
    },
    {
      icon: Globe,
      title: 'Zambia Coverage',
      description: 'Dedicated EPR compliance solutions specifically for the Zambian market',
      image: 'https://images.pexels.com/photos/8828387/pexels-photo-8828387.jpeg?auto=compress&cs=tinysrgb&w=400&h=200&fit=crop',
    },
    {
      icon: FileText,
      title: 'Easy Reporting',
      description: 'Automated reports and documentation for regulatory submissions',
      image: 'https://images.unsplash.com/photo-1748609160056-7b95f30041f0?w=400&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'Who needs to comply with EPR in Zambia?',
      answer: 'Producers, importers, and brand owners who place plastic packaging or electrical and electronic equipment (WEEE) on the Zambian market are required to comply with EPR regulations. This includes manufacturers, retailers, and distributors of packaged goods and electronics.',
    },
    {
      question: 'What materials are covered?',
      answer: 'Our EPR compliance services cover plastic packaging (PET, HDPE, PP, LDPE) and most WEEE types. For other materials, please contact us for a customized solution.',
    },
    {
      question: 'How are fees calculated?',
      answer: 'Fees are calculated based on the weight and type of materials you place on the market. Use our online calculator to get an instant estimate based on your specific material volumes.',
    },
    {
      question: 'What services does AfriCycle provide?',
      answer: 'AfriCycle provides comprehensive EPR compliance services for plastic packaging and WEEE, including producer registration, reporting, waste collection assurance, quarterly updates, and dedicated support to help you meet all regulatory requirements.',
    },
    {
      question: 'How do I get started?',
      answer: 'Getting started is easy! Simply create an account, complete the self-assessment, choose a compliance package, and our team will guide you through the rest of the process.',
    },
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      
      if (response.ok) {
        setContactSubmitted(true);
        setContactForm({ name: '', email: '', message: '' });
        toast.success('Message sent successfully!');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div data-testid="home-page">
      <SEO 
        title="AfriCycle Zambia | EPR Compliance for Plastic Packaging & WEEE"
        description="AfriCycle Zambia is your trusted partner for Extended Producer Responsibility (EPR) compliance. We provide plastic packaging and WEEE recycling solutions, waste collection services, and regulatory support in Zambia."
        keywords="AfriCycle Zambia, AfriCycle PRO, EPR compliance Zambia, plastic packaging recycling Zambia, WEEE recycling Zambia, e-waste Zambia, extended producer responsibility, waste management Zambia"
        canonicalUrl="https://africycle.pro"
      />
      {/* Hero Section */}
      <section className="hero-section relative">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="flex items-center gap-2 mb-6">
                <Leaf className="h-5 w-5 text-[#69A488]" />
                <span className="text-[#69A488] font-medium text-sm">Producer Responsibility Organization</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#056839] leading-tight mb-6">
                EPR Compliance for Plastic Packaging and WEEE Made Simple
              </h1>
              <p className="text-lg sm:text-xl text-[#3F3F3F] leading-relaxed mb-8 max-w-2xl">
                AfriCycle Zambia manages plastic packaging and WEEE (electrical and electronic equipment) EPR compliance in Zambia. 
                We handle the complexity so you can focus on growing your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/self-assessment">
                  <Button className="btn-primary text-lg px-8 py-4 h-auto rounded-full font-medium" data-testid="hero-assessment-btn">
                    Self-Assessment
                  </Button>
                </Link>
                <Link to="/services">
                  <Button className="btn-primary text-lg px-8 py-4 h-auto rounded-full font-medium" data-testid="hero-services-btn">
                    View Services
                  </Button>
                </Link>
                <Link to="/calculator">
                  <Button className="btn-primary text-lg px-8 py-4 h-auto rounded-full font-medium" data-testid="hero-estimate-btn">
                    Estimate Fees
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Rotating Recycle Element */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 rounded-full bg-[#D2E4D6]/30" />
                <div className="absolute inset-4 rounded-full bg-[#D2E4D6]/40" />
                <div className="absolute inset-8 rounded-full bg-[#D2E4D6]/50 flex items-center justify-center">
                  <Recycle className="w-40 h-40 text-[#056839] rotate-slowly" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section 
        ref={stepsAnimation.ref}
        className="py-20 bg-white" 
        data-testid="steps-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 ${stepsAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#056839] mb-4">
              GET STARTED IN 3 SIMPLE STEPS
            </h2>
            <p className="text-[#3F3F3F] text-lg max-w-2xl mx-auto">
              Our streamlined process makes EPR compliance quick and hassle-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div 
                key={step.title} 
                className={`text-center ${stepsAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.15}s` }}
                data-testid={`step-${index + 1}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#69A488] text-white mb-6 transition-transform hover:scale-110">
                  <step.icon className="h-8 w-8" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-[#69A488]">Step {index + 1}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#056839] mb-3">{step.title}</h3>
                <p className="text-[#3F3F3F] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid - Why Choose AfriCycle */}
      <section 
        ref={featuresAnimation.ref}
        className="py-20 bg-[#D2E4D6]/30" 
        data-testid="features-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 ${featuresAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#056839] mb-4">
              WHY CHOOSE AFRICYCLE ZAMBIA?
            </h2>
            <p className="text-[#3F3F3F] text-lg max-w-2xl mx-auto">
              We're committed to making plastic packaging and WEEE compliance accessible for all businesses in Zambia
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`feature-card-enhanced ${featuresAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.15}s` }}
                data-testid={`feature-${index + 1}`}
              >
                {/* Feature Image */}
                <div className="h-40 overflow-hidden">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#D2E4D6] text-[#056839] mb-4">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#056839] mb-2">{feature.title}</h3>
                  <p className="text-[#3F3F3F] text-sm leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser - Simple */}
      <section 
        ref={pricingAnimation.ref}
        className="py-20 bg-white" 
        data-testid="pricing-teaser"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-3xl md:text-4xl font-semibold text-[#056839] mb-4 ${pricingAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}>
            TRANSPARENT PRICING
          </h2>
          <p className={`text-[#3F3F3F] text-lg mb-8 max-w-2xl mx-auto ${pricingAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
            Clear, straightforward pricing for your EPR compliance needs
          </p>
          
          <div 
            className={`inline-block ${pricingAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.2s' }}
          >
            <div className="bg-[#D2E4D6] rounded-2xl px-12 py-8 mb-8 transition-all duration-300 hover:shadow-lg hover:bg-[#c5dbc9]">
              <p className="text-sm font-medium text-[#056839] mb-2">Starting from</p>
              <span className="text-5xl font-bold text-[#056839]">$2,100</span>
              <span className="text-[#3F3F3F] text-lg ml-2">/year</span>
            </div>
          </div>
          
          <p className={`text-[#6B7280] mb-8 ${pricingAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            Volume-based waste collection fees calculated separately
          </p>
          <Link to="/services">
            <Button className="btn-primary text-lg px-8" data-testid="view-packages-btn">
              View All Packages
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        ref={faqAnimation.ref}
        className="py-20 bg-[#F9FAFB]" 
        data-testid="faq-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 ${faqAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#056839] mb-4">
              FREQUENTLY ASKED QUESTIONS
            </h2>
            <p className="text-[#3F3F3F] text-lg">
              Find answers to common questions about EPR compliance in Zambia
            </p>
          </div>
          
          <div className={`${faqAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Partner Logos */}
      <section 
        ref={partnersAnimation.ref}
        className="py-16 bg-white" 
        data-testid="partners-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 ${partnersAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#056839] mb-2">
              OUR PARTNERS
            </h2>
            <p className="text-[#6B7280]">Trusted partnerships for sustainable waste management</p>
          </div>
          
          <div className={`flex flex-wrap items-center justify-center gap-12 md:gap-20 ${partnersAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            {/* GFS Logo */}
            <a 
              href="https://gfs.earth" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center transition-transform hover:scale-105"
            >
              <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center p-3 shadow-sm hover:shadow-md transition-shadow">
                <img 
                  src="/gfs-logo.png" 
                  alt="Green Forest Solutions - AfriCycle Zambia Partner for Environmental Services" 
                  className="w-full h-full object-contain"
                />
              </div>
            </a>
            
            {/* Unwaste Logo */}
            <a 
              href="https://unwaste.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center transition-transform hover:scale-105"
            >
              <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center p-3 shadow-sm hover:shadow-md transition-shadow">
                <img 
                  src="/unwaste-logo.png" 
                  alt="Unwaste - AfriCycle Zambia Partner for Waste Management Solutions" 
                  className="w-full h-full object-contain"
                />
              </div>
            </a>

            {/* ElectroCycle Zambia Logo */}
            <a 
              href="https://electrocycle.pro" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center transition-transform hover:scale-105"
            >
              <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center p-3 shadow-sm hover:shadow-md transition-shadow">
                <img 
                  src="/electrocycle-logo.png" 
                  alt="ElectroCycle Zambia - AfriCycle Partner for WEEE E-Waste Recycling" 
                  className="w-full h-full object-contain"
                />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section 
        ref={contactAnimation.ref}
        className="py-20 bg-[#D2E4D6]/30" 
        data-testid="contact-form-section"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 ${contactAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`}>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#056839] mb-4">
              GET IN TOUCH
            </h2>
            <p className="text-[#3F3F3F] text-lg">
              Have questions? Send us a message and we'll get back to you shortly.
            </p>
          </div>
          
          <div className={`bg-white rounded-2xl shadow-sm p-8 ${contactAnimation.isVisible ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            {contactSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#D2E4D6] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-[#056839]" />
                </div>
                <h3 className="text-xl font-semibold text-[#056839] mb-2">Thank You!</h3>
                <p className="text-[#3F3F3F]">We will get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Name</label>
                  <Input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Your name"
                    required
                    className="form-input"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Email</label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    className="form-input"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Message</label>
                  <Textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="How can we help you?"
                    rows={4}
                    required
                    className="form-input"
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-primary w-full text-lg py-3 h-auto"
                  disabled={contactLoading}
                  data-testid="contact-submit"
                >
                  {contactLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Send className="h-5 w-5 mr-2" />
                  )}
                  Send Message
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#056839]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
                READY TO GET COMPLIANT?
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Join producers who trust AfriCycle Zambia for their plastic packaging and WEEE EPR compliance needs
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button 
                    className="bg-white text-[#056839] hover:bg-gray-100 rounded-full px-8 py-3 font-medium text-lg h-auto border-2 border-white transition-all duration-300"
                    data-testid="cta-calculator-btn"
                  >
                    Calculate Your Fees
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="border-2 border-white text-white hover:bg-white hover:text-[#056839] rounded-full px-8 py-3 font-medium text-lg h-auto bg-transparent transition-all duration-300"
                    data-testid="cta-register-btn"
                  >
                    Create Free Account
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Illustration */}
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full bg-white/10 flex items-center justify-center">
                  <div className="w-48 h-48 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle className="w-24 h-24 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
